import { GoBackButton } from "lego-gui-lib";
import { AccessContext } from "lego-gui-lib/lib";
import { TitleRow } from "lego-gui-lib/lib/components/Views/TitleRow";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect } from "react";
import { BreadcrumbsItem } from "react-breadcrumbs-dynamic";
import { useTranslation } from "react-i18next";
import { Route, useHistory, useParams, useRouteMatch } from "react-router";
import { authStoreContext } from "../../../core/mainStore";
import { serviceProfileAllows } from "../../serviceProfile/validator";
import { SanContext } from "../schema";
import { TabContent, TabData } from "../tabs/TabContent";
import { ConfigViewTabHeader } from "./ConfigViewTabHeader";
import { FormView } from "./FormView";

export const Config = observer(() => {
  const history = useHistory();
  const auth = useContext(authStoreContext);
  const match = useRouteMatch();
  const { t } = useTranslation();
  const SAN = useContext(SanContext);

  const title = `${t("common:config")} ${t("hns:san:domain")} (${
    SAN.oneDataSource.one?.mainNumber
  })`;
  const baseUrl = match.url;

  const getParentUrl = (url: string) =>
    baseUrl.substring(0, url.lastIndexOf("/"));

  const goBack = () => {
    history.goBack();
  };

  const id = useParams();

  /** Cleanup effect to reload SAN.oneDataSource.one to prevent storing invalid SAN data in session, when bad request occurs */
  useEffect(() => {
    return () => {
      SAN.oneDataSource.findOne(id);
    };
  }, []);

  const tabs: TabData[] = [
    {
      label: t("common:general"),
      path: "/general",
      Element: (
        <FormView
          id={id}
          groups={[
            "common:main",
            "common:general",
            "hns:san:temporaryDeactivation"
          ]}
        />
      )
    },
    {
      label: t("common:routing"),
      path: "/routing",
      Element: <FormView id={id} groups={["hns:san:callRouting"]} />
    },
    {
      label: t("common:interactions"),
      path: "/interactions",
      Element: (
        <FormView
          id={id}
          groups={[
            "hns:san:accessByPin",
            "hns:san:greetings",
            "hns:san:priceInformation",
            "hns:languageSelect:domain"
          ]}
        />
      ),
      if: (ctx: AccessContext) =>
        serviceProfileAllows(ctx, "featInteractSelectLang") ||
        serviceProfileAllows(ctx, "featInteractPriceAnn") ||
        serviceProfileAllows(ctx, "featInteractWelcomeAnn") ||
        serviceProfileAllows(ctx, "featInteractPin")
    },
    {
      label: t("common:features"),
      path: "/features",
      Element: (
        <FormView
          id={id}
          groups={[
            "hns:san:aNumberPortability",
            "hns:san:callQueueing",
            "hns:san:presentation",
            "hns:san:smsNotifications",
            "hns:callLog:domain",
            "hns:callLog:reporting"
          ]}
        />
      )
    },
    {
      label: t("hns:san:callBlocking"),
      path: "/callBlocking",
      Element: (
        <FormView
          id={id}
          groups={["hns:san:incomingCalls", "hns:san:outgoingCalls"]}
        />
      ),
      if: (ctx: AccessContext) =>
        serviceProfileAllows(ctx, "featScreenOut") ||
        serviceProfileAllows(ctx, "featScreenIn")
    },
    {
      label: t("common:limits"),
      path: "/limits",
      Element: (
        <FormView
          id={id}
          groups={["hns:san:numberOfCalls", "hns:san:durationRelated"]}
        />
      ),
      if: (ctx: AccessContext) =>
        serviceProfileAllows(ctx, "featMaxCallsSan") ||
        serviceProfileAllows(ctx, "featMaxCallDuration")
    },
    {
      label: t("advanced"),
      path: "/advanced",
      if: () => auth.isAdmin(),
      Element: (
        <FormView
          id={id}
          groups={[
            "hns:serviceProfile:domain",
            "hns:san:billingInformation",
            "hns:san:networkRelatedConfig",
            "hns:san:numberValidationOnGui",
            "hns:san:adminNotes"
          ]}
        />
      )
    }
  ];

  return (
    <>
      <BreadcrumbsItem to={getParentUrl(baseUrl)}>
        {t("hns:san:domain") + " (" + SAN.oneDataSource.one?.mainNumber + ")"}
      </BreadcrumbsItem>
      <BreadcrumbsItem to={baseUrl + "/config"}>
        {t("common:config")}
      </BreadcrumbsItem>
      <TitleRow
        classes={{ titleText: "", title: "" }}
        title={title}
        prefix={<GoBackButton goBack={goBack} />}
      />
      <Route
        path={baseUrl}
        render={({ location }) => (
          <>
            <ConfigViewTabHeader
              baseUrl={baseUrl}
              tabs={tabs}
              value={location.pathname}
            />
            <TabContent baseUrl={baseUrl} tabs={tabs} />
          </>
        )}
      />
    </>
  );
});
